import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStatus from "../../hooks/useAuthStatus";
import { getAssets, getUserWallet } from "../../api/apiServices";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

// ─── Skeleton primitives ──────────────────────────────────────
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 rounded-xl ${className}`} />
);

function PortfolioCardSkeleton() {
  return (
    <div className="mx-5 mt-4 rounded-2xl p-5 mb-5 overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0F4C35 0%, #1A6B4A 50%, #0F4C35 100%)" }}>
      <div className="h-3 w-36 rounded-full bg-white/20 animate-pulse mb-3" />
      <div className="h-9 w-48 rounded-xl bg-white/20 animate-pulse mb-2" />
      <div className="h-3 w-40 rounded-full bg-white/15 animate-pulse mb-5" />
      <div className="grid grid-cols-2 gap-3">
        <div className="h-10 rounded-xl bg-white/20 animate-pulse" />
        <div className="h-10 rounded-xl bg-white/10 animate-pulse" />
      </div>
    </div>
  );
}

function AssetCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl px-4 py-4 border border-slate-100 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="w-11 h-11 rounded-full" />
          <div className="space-y-1.5">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-2.5 w-20" />
          </div>
        </div>
        <div className="items-end flex flex-col gap-1.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Skeleton className="h-2.5 w-24" />
        <Skeleton className="h-2.5 w-16" />
      </div>
      <Skeleton className="h-1.5 w-full rounded-full" />
      <div className="flex gap-2">
        <Skeleton className="flex-1 h-9 rounded-xl" />
        <Skeleton className="flex-1 h-9 rounded-xl" />
        <Skeleton className="flex-1 h-9 rounded-xl" />
      </div>
    </div>
  );
}

const getHoldingColor = (symbol) => {
  const colors = { BTC: "#F7931A", ETH: "#627EEA", USDT: "#26A17B", BNB: "#F3BA2F", SOL: "#9945FF", XRP: "#00AAE4", DOGE: "#C2A633", ADA: "#0033AD" };
  return colors[symbol] || "#64748B";
};

const getHoldingIcon = (symbol) => {
  const icons = { BTC: "ri-bitcoin-line", ETH: "ri-eth-line", USDT: "ri-money-dollar-circle-line", BNB: "ri-coin-line", SOL: "ri-star-line" };
  return icons[symbol] || "ri-currency-line";
};

export default function Wallet() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [walletData, setWalletData] = useState(null);
  const [liveRates, setLiveRates] = useState({});

  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const portfolioInitialLoad = useRef(false);

  const parseCurrency = (str) => {
    if (!str) return 0;
    return parseFloat(str.replace(/[₦$,%]/g, ""));
  };

  const handleProtectedAction = (path) => {
    if (!isAuthenticated) navigate("/auth");
    else navigate(path);
  };

  const formatNaira = (amount) =>
    `₦${amount.toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;

  // ── Fetch wallet + internal rates (same as Home) ──────────
  useEffect(() => {
    if (!isAuthenticated) { setPortfolioLoading(false); return; }

    const fetchData = async () => {
      if (!portfolioInitialLoad.current) setPortfolioLoading(true);
      try {
        const [walletRes, assetsData] = await Promise.all([
          getUserWallet(),
          getAssets(),
        ]);

        // Build rate map from internal assets API
        const rateMap = {};
        assetsData.forEach(asset => {
          rateMap[asset.symbol] = {
            price: parseCurrency(asset.price),
            change: parseCurrency(asset.change),
            icon: asset.icon,
          };
        });
        setLiveRates(rateMap);

        if (walletRes.status === true) {
          setWalletData(walletRes.data);
        }
      } catch (err) {
        console.error("Failed to load wallet:", err);
      } finally {
        setPortfolioLoading(false);
        portfolioInitialLoad.current = true;
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // ── Computed values ───────────────────────────────────────
  const totalPortfolio = useMemo(() => {
    if (!walletData) return 0;
    let cryptoValue = 0;
    walletData.wallets?.forEach(wallet => {
      const rate = liveRates[wallet.currency]?.price || 0;
      cryptoValue += (wallet.balance || 0) * rate;
    });
    return (walletData.nairaBalance || 0) + cryptoValue;
  }, [walletData, liveRates]);

  const realHoldings = useMemo(() => {
    if (!walletData?.wallets) return [];
    return walletData.wallets
      .filter(w => (w.balance || 0) > 0)
      .map(wallet => {
        const assetInfo = liveRates[wallet.currency];
        const rate = assetInfo?.price || 0;
        return {
          symbol: wallet.currency,
          name: wallet.currency === "USDT" ? "Tether" : wallet.currency,
          amount: wallet.balance || 0,
          valueNGN: (wallet.balance || 0) * rate,
          change24h: assetInfo?.change || 0,
          iconUrl: assetInfo?.icon || null,
          color: getHoldingColor(wallet.currency),
          icon: getHoldingIcon(wallet.currency),
        };
      })
      .sort((a, b) => b.valueNGN - a.valueNGN);
  }, [walletData, liveRates]);

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-5 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-800 font-bold text-xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              My Wallet
            </h1>
            <p className="text-slate-400 text-sm">All your crypto holdings</p>
          </div>
          {!isAuthenticated && (
            <button onClick={() => navigate("/auth")}
              className="px-5 py-2 text-sm font-semibold text-emerald-600 border border-emerald-200 rounded-xl hover:bg-emerald-50 transition-colors">
              Sign In
            </button>
          )}
        </div>
      </div>

      {/* ── Portfolio Card ── */}
      {portfolioLoading && isAuthenticated ? (
        <PortfolioCardSkeleton />
      ) : (
        <div className="mx-5 mt-4 rounded-2xl p-5 relative overflow-hidden mb-5"
          style={{ background: "linear-gradient(135deg, #0F4C35 0%, #1A6B4A 50%, #0F4C35 100%)" }}>

          {!isAuthenticated && (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-md rounded-2xl flex items-center justify-center z-20">
              <div className="text-center px-6">
                <i className="ri-lock-2-line text-white text-5xl mb-4" />
                <p className="text-white font-semibold text-lg mb-1">Sign in to view portfolio</p>
                <p className="text-emerald-200 text-sm mb-6">Your assets are private and secure</p>
                <button onClick={() => navigate("/auth")}
                  className="px-8 py-3 bg-white text-emerald-700 rounded-2xl font-semibold hover:bg-emerald-50 transition-colors">
                  Login to Unlock
                </button>
              </div>
            </div>
          )}

          <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(30%,-30%)" }} />

          <div className="relative z-10">
            <p className="text-emerald-200 text-xs font-medium mb-1">Total Portfolio Value</p>
            <p className="text-white text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {isAuthenticated ? formatNaira(totalPortfolio) : "₦ ••••••"}
            </p>
            <p className="text-emerald-300 text-xs mt-1 mb-4">
              NGN Balance:{" "}
              <span className="text-white font-semibold">
                {isAuthenticated ? formatNaira(walletData?.nairaBalance || 0) : "₦ ••••"}
              </span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => handleProtectedAction("/deposit")}
                className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/20 text-white text-sm font-medium hover:bg-white/30 transition-colors">
                <i className="ri-arrow-down-circle-line" /> Deposit
              </button>
              <button onClick={() => handleProtectedAction("/withdraw-crypto")}
                className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-medium hover:bg-white/20 transition-colors">
                <i className="ri-arrow-up-circle-line" /> Withdraw
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assets Section ── */}
      <div className="px-5 relative">
        <h2 className="text-slate-800 font-bold text-sm mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Assets
        </h2>

        {!isAuthenticated && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-md z-20 flex items-center justify-center rounded-3xl border border-slate-200">
            <div className="text-center px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
                <i className="ri-lock-2-line text-slate-400 text-3xl" />
              </div>
              <p className="font-semibold text-slate-700 text-lg mb-4">Login to see your assets</p>
              <button onClick={() => navigate("/auth")}
                className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-2xl text-sm flex items-center gap-2 mx-auto transition-colors">
                <i className="ri-login-box-line" /> Sign in to Unlock
              </button>
            </div>
          </div>
        )}

        <div className={`space-y-3 ${!isAuthenticated ? "blur-sm pointer-events-none" : ""}`}>
          {portfolioLoading && isAuthenticated ? (
            // Show 3 skeleton asset cards while loading
            [...Array(3)].map((_, i) => <AssetCardSkeleton key={i} />)
          ) : realHoldings.length > 0 ? (
            realHoldings.map((h) => {
              const pct = totalPortfolio > 0
                ? ((h.valueNGN / totalPortfolio) * 100).toFixed(1)
                : 0;
              return (
                <div key={h.symbol} className="bg-white rounded-2xl px-4 py-4 border border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden"
                        style={{ background: `${h.color}18` }}>
                        {h.iconUrl
                          ? <img src={h.iconUrl} alt={h.symbol} className="w-7 h-7 object-contain" />
                          : <i className={`${h.icon} text-xl`} style={{ color: h.color }} />}
                      </div>
                      <div>
                        <p className="text-slate-800 text-sm font-bold">{h.symbol}</p>
                        <p className="text-slate-400 text-xs">{h.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-800 text-sm font-bold">{formatNaira(h.valueNGN)}</p>
                      <p className="text-slate-400 text-xs">{h.amount} {h.symbol}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400 text-xs">{pct}% of portfolio</span>
                    <span className={`text-xs font-semibold ${h.change24h >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {h.change24h >= 0 ? "+" : ""}{h.change24h}% 24h
                    </span>
                  </div>

                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: h.color }} />
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button onClick={() => handleProtectedAction("/buy")}
                      className="flex-1 py-2.5 rounded-xl text-xs font-semibold transition-colors"
                      style={{ background: `${h.color}15`, color: h.color }}>
                      Buy
                    </button>
                    <button onClick={() => handleProtectedAction("/sell")}
                      className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200 transition-colors">
                      Sell
                    </button>
                    <button onClick={() => handleProtectedAction("/withdraw-crypto")}
                      className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200 transition-colors">
                      Send
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-white rounded-2xl p-10 text-center border border-slate-100">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-wallet-3-line text-slate-400 text-2xl" />
              </div>
              <p className="text-slate-600 font-semibold mb-1">No assets yet</p>
              <p className="text-slate-400 text-sm mb-4">Deposit or buy crypto to get started</p>
              <button onClick={() => handleProtectedAction("/deposit")}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-sm transition-colors">
                Deposit Now
              </button>
            </div>
          )}
        </div>
      </div>

      <MobileBottomNav />
    </div>
  );
}
